import React, { useEffect, useMemo, useState } from "react";
import CsvDownloader from "react-csv-downloader";
import {
  ArrowUpRight,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  CopyCheck,
  FileSpreadsheet,
  BookOpen,
  CirclePlay,
  Search,
  SearchX,
  UsersRound,
  Users,
} from "lucide-react";
import { TbFileTypeCsv } from "react-icons/tb";
import { useNavigate } from "react-router-dom";

import { validateEmail } from "../../utils/helpers";
import { Button } from "../ui/button";
import { EmptyState } from "../ui/empty-state";
import { HelpVideoDialog } from "../ui/help-video-dialog";
import { Input } from "../ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import {
  Table as UiTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tooltip } from "../ui/tooltip";
import "./table.css";

const HELP_ARTICLE_URL = "https://groupboss.io/help/install-groupboss-chrome-extension/";
const SETUP_VIDEO_URL = "https://vimeo.com/1147895186?fl=ip&fe=ec";

const EMAIL_FIELDS = ["email", "email_address", "profile_email"];
const DATE_FIELDS = ["created_at", "createdAt", "time", "timestamp", "date"];
const PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const asText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return value.name || value.title || "";
  return String(value);
};

const firstValue = (item, fields) =>
  fields
    .map((field) => item?.[field])
    .find((value) => value !== null && value !== undefined && value !== "");

const getLeadItems = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.leads)) return value.leads;
  if (Array.isArray(value?.data)) return value.data;
  if (value?.data) return getLeadItems(value.data);
  return [];
};

const getAnswerEmail = (item) => {
  const qna = item?.membership_qna;
  return [item, qna]
    .filter((source) => source && typeof source === "object")
    .flatMap((source) => [
      firstValue(source, EMAIL_FIELDS),
      source.answer1,
      source.answer2,
      source.answer3,
    ])
    .map(asText)
    .find((value) => validateEmail(value)) || "";
};

const getQuestions = (item) => {
  const qna = item?.membership_qna;
  const questionSource = qna && typeof qna === "object" && !Array.isArray(qna) ? qna : item;
  const questionList = Array.isArray(item?.questions) && item.questions.length
    ? item.questions
    : Array.isArray(questionSource?.questions)
      ? questionSource.questions
      : null;

  if (questionList) {
    return questionList
      .map((question) => ({
        question: asText(question?.question ?? question?.text),
        answer: asText(question?.answer ?? question?.value),
      }))
      .filter(({ question, answer }) => question || answer);
  }

  return [1, 2, 3]
    .map((number) => ({
      question: asText(questionSource?.[`question${number}`]),
      answer: asText(questionSource?.[`answer${number}`]),
    }))
    .filter(({ question, answer }) => question || answer);
};

const getMemberSince = (item) => {
  const qna = item?.membership_qna;
  return firstValue(
    qna && typeof qna === "object" ? qna : {},
    ["member_since", "memberSince", "joined_at", "joinedAt"]
  ) || firstValue(item, ["member_since", "memberSince", "joined_at", "joinedAt"]);
};

const titleCase = (value) =>
  value
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getStatus = (item, email) => {
  const rawStatus = asText(firstValue(item, ["status", "sync_status", "lead_status"]));
  if (!rawStatus) return email ? "Synced" : "No email";

  const normalizedStatus = rawStatus.toLowerCase();
  if (["success", "successful", "synced", "sync", "sent"].includes(normalizedStatus)) return "Synced";
  if (["failed", "failure", "error"].includes(normalizedStatus)) return "Failed";
  if (["no_email", "no email", "missing email"].includes(normalizedStatus)) return "No email";
  return titleCase(rawStatus);
};

export const normalizeLeads = (value) =>
  getLeadItems(value).map((item, index) => {
    const fullName = asText(firstValue(item, ["member_name", "full_name", "name", "lead_name"])) || "Unknown lead";
    const email = getAnswerEmail(item) || asText(firstValue(item, EMAIL_FIELDS));
    const groupValue = firstValue(item, ["source_group", "sourceGroup", "source_group_name", "group_name", "group"]);
    const groupId = item?.group_id ?? item?.source_group_id ?? item?.groupId ?? (
      typeof groupValue === "object" ? groupValue?.id ?? groupValue?.group_id : ""
    );
    const sourceGroup = asText(groupValue) || "Unknown group";
    const destination = asText(firstValue(item, ["destination", "destination_name", "destination_integration", "integration", "integration_name", "provider"])) || "—";
    const createdAt = firstValue(item, DATE_FIELDS) || "";
    const memberSince = getMemberSince(item) || createdAt;
    const questions = getQuestions(item);
    const id = asText(firstValue(item, ["id", "lead_id"])) || `${fullName}-${email}-${index}`;

    return {
      id,
      fullName,
      email,
      sourceGroup,
      destination,
      status: getStatus(item, email),
      createdAt,
      memberSince,
      questions,
      profileUrl: asText(firstValue(item, ["profile_url", "profileUrl", "facebook_url", "url"])),
      groupUrl: groupId ? `https://www.facebook.com/groups/${encodeURIComponent(groupId)}` : "",
      raw: item,
    };
  });

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateKey = (date) => {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return asText(value) || "—";
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};

export const formatLeadTime = (value) => {
  const date = parseDate(value);
  if (!date) return asText(value) || "—";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) === 1 ? "" : "s"} ago`;
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(date);
};

export const formatMemberSince = (value) => {
  const date = parseDate(value);
  if (!date) return asText(value) || "—";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export const filterByDate = (rows, startDate, endDate) => {
  const start = startDate || "";
  const end = endDate || start;
  return rows.filter((row) => {
    if (!start && !end) return true;
    const dateKey = toDateKey(parseDate(row.memberSince || row.createdAt));
    return dateKey && (!start || dateKey >= start) && (!end || dateKey <= end);
  });
};

const getExportRows = (rows) =>
  rows.map((row) => ({
    "Full Name": row.fullName,
    Email: row.email,
    "Source Group": row.sourceGroup,
    "Member Since": formatLeadTime(row.memberSince || row.createdAt),
    "Question 1": row.questions[0]?.question || "",
    "Answer 1": row.questions[0]?.answer || "",
    "Question 2": row.questions[1]?.question || "",
    "Answer 2": row.questions[1]?.answer || "",
    "Question 3": row.questions[2]?.question || "",
    "Answer 3": row.questions[2]?.answer || "",
  }));

const exportColumns = [
  "Full Name", "Email", "Source Group", "Member Since",
  "Question 1", "Answer 1", "Question 2", "Answer 2", "Question 3", "Answer 3",
].map((id) => ({ id, displayName: id }));

const getAudienceRows = (rows) =>
  rows
    .filter((row) => validateEmail(row.email))
    .map((row) => {
      const [firstName = "", ...lastName] = row.fullName.split(/\s+/);
      return { "First Name": firstName, "Last Name": lastName.join(" "), Email: row.email };
    });

const audienceColumns = [
  { id: "First Name", displayName: "first_name" },
  { id: "Last Name", displayName: "last_name" },
  { id: "Email", displayName: "email" },
];

const exportExcel = async (rows) => {
  const XLSX = await import("xlsx");
  const worksheet = XLSX.utils.json_to_sheet(getExportRows(rows));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
  XLSX.writeFile(workbook, "groupboss-leads.xlsx");
};

const getPaginationMeta = (value, leadCount) => {
  const hasEnvelope = value && !Array.isArray(value) && typeof value === "object";
  const total = hasEnvelope ? Number(value.total) : NaN;
  const limit = hasEnvelope ? Number(value.limit) : NaN;
  const offset = hasEnvelope ? Number(value.offset) : NaN;

  return {
    hasServerPagination: hasEnvelope && Number.isFinite(total) && Number.isFinite(limit) && limit > 0,
    total: Number.isFinite(total) && total >= 0 ? total : leadCount,
    limit: Number.isFinite(limit) && limit > 0 ? limit : PAGE_SIZE,
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
    hasMore: Boolean(hasEnvelope && value.has_more),
  };
};

const getCalendarDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const firstCell = new Date(year, month, 1 - firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(firstCell);
    day.setDate(firstCell.getDate() + index);
    return day;
  });
};

const parseDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const shiftMonth = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);

const getSelectedDayCount = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  return Math.max(0, Math.round((end - start) / 86400000) + 1);
};

const CalendarDay = ({ date, monthDate, startDate, endDate, onSelect }) => {
  const key = toDateKey(date);
  const isOutsideMonth = date.getMonth() !== monthDate.getMonth();
  const isStart = key === startDate;
  const isEnd = key === endDate;
  const isInRange = Boolean(startDate && endDate && key > startDate && key < endDate);

  return (
    <Button
      aria-label={date.toLocaleDateString(undefined, { dateStyle: "full" })}
      aria-pressed={isStart || isEnd}
      className={`lead-calendar-day${isOutsideMonth ? " is-outside" : ""}${isInRange ? " is-in-range" : ""}${isStart ? " is-start" : ""}${isEnd ? " is-end" : ""}`}
      onClick={() => onSelect(key)}
      size="default"
      type="button"
      variant="ui"
    >
      {date.getDate()}
    </Button>
  );
};

function DateRangeControl({ startDate, endDate, onChange }) {
  const [open, setOpen] = useState(false);
  const [monthDate, setMonthDate] = useState(() => parseDateKey(startDate) || new Date());
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const displayStartDate = open ? draftStartDate : startDate;
  const displayEndDate = open ? draftEndDate : endDate;
  const display = displayStartDate
    ? `${formatDate(displayStartDate)} to ${displayEndDate ? formatDate(displayEndDate) : "----"}`
    : "Select dates";
  const selectedDayCount = getSelectedDayCount(displayStartDate, displayEndDate);

  useEffect(() => {
    if (open) {
      setDraftStartDate(startDate);
      setDraftEndDate(endDate);
      setMonthDate(parseDateKey(startDate) || new Date());
    }
  }, [endDate, open, startDate]);

  const selectDate = (nextDate) => {
    if (!draftStartDate || draftEndDate) {
      setDraftStartDate(nextDate);
      setDraftEndDate("");
      return;
    }

    if (nextDate < draftStartDate) {
      setDraftStartDate(nextDate);
      setDraftEndDate(draftStartDate);
    } else {
      setDraftEndDate(nextDate);
    }
  };

  const getQuickRange = (days) => {
    const end = toDateKey(new Date());
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    return { end, start: toDateKey(start) };
  };

  const setQuickRange = (days) => {
    const { end, start } = getQuickRange(days);
    setDraftStartDate(start);
    setDraftEndDate(end);
  };

  const isQuickRangeActive = (days) => {
    const { end, start } = getQuickRange(days);
    return draftStartDate === start && draftEndDate === end;
  };

  const applyDateRange = () => {
    onChange(draftStartDate, draftEndDate);
    setOpen(false);
  };

  return (
    <div className="lead-date-field">
      <span className="lead-date-label">Date Range{selectedDayCount ? ` (Days selected: ${selectedDayCount})` : ""}</span>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger
          render={
            <button className="lead-date-trigger" type="button">
              <Calendar aria-hidden="true" size={18} />
              <span>{display}</span>
            </button>
          }
        />
        <PopoverContent className="lead-date-popover">
          <div className="lead-calendar-header">
            <Button aria-label="Previous month" className="lead-calendar-nav" onClick={() => setMonthDate((current) => shiftMonth(current, -1))} size="default" type="button" variant="ui"><ChevronLeft size={18} /></Button>
            <strong>{monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</strong>
            <Button aria-label="Next month" className="lead-calendar-nav" onClick={() => setMonthDate((current) => shiftMonth(current, 1))} size="default" type="button" variant="ui"><ChevronRight size={18} /></Button>
          </div>
          <div className="lead-calendar-weekdays" aria-hidden="true">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => <span key={day}>{day}</span>)}
          </div>
          <div className="lead-calendar-grid">
            {getCalendarDays(monthDate).map((date) => <CalendarDay endDate={draftEndDate} key={toDateKey(date)} monthDate={monthDate} onSelect={selectDate} startDate={draftStartDate} date={date} />)}
          </div>
          <div className="lead-calendar-footer">
            <div className="lead-calendar-footer-left">
              <Button className="lead-quick-range" onClick={() => setQuickRange(7)} size="sm" type="button" variant={isQuickRangeActive(7) ? "primary" : "secondary"}>Last 7 days</Button>
              <Button className="lead-quick-range" onClick={() => setQuickRange(30)} size="sm" type="button" variant={isQuickRangeActive(30) ? "primary" : "secondary"}>Last 30 days</Button>
            </div>
            <div className="lead-calendar-footer-right">
              <Button className="lead-apply-date" onClick={applyDateRange} size="sm" type="button" variant="ui">Apply</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function LeadRow({ lead, copiedProfileLeadId, onCopy }) {
  return (
    <TableRow>
      <TableCell>
        <div className="lead-combined-cell">
          <div className="lead-name-cell">
            <strong className="lead-full-name" title={lead.fullName}>{lead.fullName}</strong>
            <Tooltip content={copiedProfileLeadId === lead.id ? "Copied" : "Copy profile URL"} side="top">
              <Button
                aria-label={copiedProfileLeadId === lead.id ? `Profile URL copied for ${lead.fullName}` : `Copy profile URL for ${lead.fullName}`}
                className="copy-profile-button"
                disabled={!lead.profileUrl}
                onClick={() => onCopy(lead.profileUrl, lead.id)}
                size="icon-sm"
                type="button"
                variant="ui"
              >
                {copiedProfileLeadId === lead.id ? <CopyCheck aria-hidden="true" size={13} /> : <Copy aria-hidden="true" size={13} />}
              </Button>
            </Tooltip>
          </div>
          <span className="lead-email-text" title={lead.email}>{lead.email || "Not available"}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="lead-group-cell">
          <span className="source-group-tag lead-group-tag" title={lead.sourceGroup}>{lead.sourceGroup}</span>
          {lead.groupUrl && (
            <Tooltip content="Open group on Facebook" side="top">
              <Button aria-label={`Open ${lead.sourceGroup} on Facebook`} className="open-group-link" onClick={() => window.open(lead.groupUrl, "_blank", "noopener,noreferrer")} size="icon-xs" type="button" variant="ui">
                <ArrowUpRight aria-hidden="true" size={14} />
              </Button>
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell className="lead-member-cell">{formatMemberSince(lead.memberSince || lead.createdAt)}</TableCell>
      {[0, 1, 2].map((index) => {
        const item = lead.questions[index] || {};
        return (
          <TableCell className="lead-qna-cell" key={`${lead.id}-qna-${index}`}>
            <div className="lead-qna-question">{item.question || "—"}</div>
            <div className="lead-qna-answer">{item.answer || "—"}</div>
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function PageNumber({ page, currentPage, onClick }) {
  const pageNumber = page + 1;

  return (
    <Button
      aria-current={currentPage === page ? "page" : undefined}
      aria-label={`Go to page ${pageNumber}`}
      className={`lead-page-number${currentPage === page ? " is-current" : ""}`}
      disabled={currentPage === page}
      onClick={() => onClick(page)}
      size="sm"
      type="button"
      variant="ui"
    >
      {pageNumber}
    </Button>
  );
}

function Table({ tableData, isLoading, error, redirect, getTableData }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("All Groups");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [copiedProfileLeadId, setCopiedProfileLeadId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isSetupVideoOpen, setIsSetupVideoOpen] = useState(false);

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  useEffect(() => {
    if (redirect) navigate("/login");
  }, [navigate, redirect]);

  const leads = useMemo(() => normalizeLeads(tableData), [tableData]);
  const pagination = useMemo(() => getPaginationMeta(tableData, leads.length), [leads.length, tableData]);
  const groups = useMemo(() => ["All Groups", ...new Set(leads.map((lead) => lead.sourceGroup))], [leads]);
  const visibleLeads = useMemo(() => {
    const term = search.trim().toLowerCase();
    return filterByDate(leads, startDate, endDate).filter((lead) => {
      const matchesGroup = group === "All Groups" || lead.sourceGroup === group;
      const matchesSearch = !term || `${lead.fullName} ${lead.email} ${lead.sourceGroup}`.toLowerCase().includes(term);
      return matchesGroup && matchesSearch;
    });
  }, [endDate, group, leads, search, startDate]);
  const exportRows = visibleLeads;
  const audienceRows = useMemo(() => getAudienceRows(exportRows), [exportRows]);
  const loading = isLoading || tableData === undefined;
  const totalItemCount = pagination.hasServerPagination ? pagination.total : visibleLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalItemCount / pageSize));
  const currentPage = pagination.hasServerPagination ? Math.floor(pagination.offset / pageSize) : page;
  const selectedDateDays = startDate
    ? endDate
      ? getSelectedDayCount(startDate, endDate)
      : 1
    : 0;
  const dateRangeLabel = selectedDateDays > 1 ? `${selectedDateDays} days` : "day";
  const hasActiveFilters = Boolean(
    search.trim() ||
    group !== "All Groups" ||
    startDate ||
    endDate
  );
  const paginatedLeads = pagination.hasServerPagination
    ? visibleLeads
    : visibleLeads.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  useEffect(() => {
    setPage(0);
  }, [endDate, group, search, startDate]);

  const clearFilters = () => {
    setSearch("");
    setGroup("All Groups");
    setStartDate("");
    setEndDate("");
  };

  const updateDateRange = (nextStart, nextEnd) => {
    if (nextStart && nextEnd && nextEnd < nextStart) {
      setStartDate(nextEnd);
      setEndDate(nextStart);
      return;
    }
    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  const shiftDateRange = (direction) => {
    const start = parseDate(startDate);
    const end = parseDate(endDate || startDate);
    if (!start) return;
    const span = end ? Math.max(1, Math.round((end - start) / 86400000) + 1) : 1;
    const nextStart = new Date(start);
    nextStart.setDate(nextStart.getDate() + direction * span);
    const nextEnd = new Date(nextStart);
    nextEnd.setDate(nextEnd.getDate() + span - 1);
    setStartDate(toDateKey(nextStart));
    setEndDate(endDate ? toDateKey(nextEnd) : "");
  };

  const goToPage = (nextPage) => {
    const boundedPage = Math.min(Math.max(nextPage, 0), totalPages - 1);
    setPage(boundedPage);
    if (pagination.hasServerPagination) {
      getTableData({ limit: pageSize, offset: boundedPage * pageSize });
    }
  };

  const updatePageSize = (value) => {
    const nextPageSize = Number(value);
    if (!PAGE_SIZE_OPTIONS.includes(nextPageSize)) return;

    setPageSize(nextPageSize);
    setPage(0);
    if (pagination.hasServerPagination) {
      getTableData({ limit: nextPageSize, offset: 0 });
    }
  };

  const copyProfileUrl = async (value, leadId) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedProfileLeadId(leadId);
      window.setTimeout(() => setCopiedProfileLeadId((currentLeadId) => currentLeadId === leadId ? null : currentLeadId), 1400);
    } catch {
      // Clipboard access can be unavailable in embedded or insecure contexts.
    }
  };

  return (
    <main className="leads-page">
      <section className="leads-shell" aria-labelledby="leads-title">
        <header className="leads-heading dashboard-page-heading">
          <h1 className="dashboard-page-title" id="leads-title">Leads</h1>
          <div className="leads-heading-actions">
            <CsvDownloader columns={audienceColumns} datas={audienceRows} filename="groupboss-custom-audience">
              <Button className="lead-action-button audience-button" disabled={!audienceRows.length} size="default" type="button"><span aria-hidden="true" className="lead-action-icon"><Users size={16} /></span><span className="lead-action-label">Create FB Custom and Lookalike Audience</span></Button>
            </CsvDownloader>
            <Button className="lead-action-button excel-button" disabled={!exportRows.length} onClick={() => exportExcel(exportRows)} size="default" type="button"><span aria-hidden="true" className="lead-action-icon"><FileSpreadsheet size={16} /></span><span className="lead-action-label">Export Excel</span></Button>
            <CsvDownloader columns={exportColumns} datas={getExportRows(exportRows)} filename="groupboss-leads">
              <Button className="lead-action-button download-button" disabled={!exportRows.length} size="default" type="button"><span aria-hidden="true" className="lead-action-icon"><TbFileTypeCsv size={16} /></span><span className="lead-action-label">Export CSV</span></Button>
            </CsvDownloader>
          </div>
        </header>

        <div className="leads-toolbar">
          <div className="leads-toolbar-left">
            <Select className="lead-select-control" onValueChange={setGroup} value={group}>
              <SelectTrigger aria-label="Filter by group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {groups.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="lead-date-controls">
              <DateRangeControl endDate={endDate} onChange={updateDateRange} startDate={startDate} />
              <Tooltip content={startDate ? `Previous ${dateRangeLabel}` : "Select a date first"} side="top">
                <Button aria-label="Previous date range" className="lead-nav-button" disabled={!startDate} onClick={() => shiftDateRange(-1)} size="icon-sm" type="button" variant="ui"><ChevronLeft size={17} /></Button>
              </Tooltip>
              <Tooltip content={startDate ? `Next ${dateRangeLabel}` : "Select a date first"} side="top">
                <Button aria-label="Next date range" className="lead-nav-button" disabled={!startDate} onClick={() => shiftDateRange(1)} size="icon-sm" type="button" variant="ui"><ChevronDown className="lead-next-chevron" size={17} /></Button>
              </Tooltip>
              <Tooltip content={hasActiveFilters ? "Reset filters" : "No filters to reset"} side="top">
                <Button className="lead-clear-button" disabled={!hasActiveFilters} onClick={clearFilters} size="sm" type="button" variant="ui">Reset</Button>
              </Tooltip>
            </div>
          </div>
          <label className="lead-search-wrap">
            <span className="sr-only">Search leads</span>
            <Input
              aria-label="Search leads"
              className="lead-search-input"
              classNames={{ input: "lead-search-input", root: "lead-search-input-shell" }}
              leftIcon={<Search size={17} />}
              onValueChange={setSearch}
              placeholder="Search leads"
              value={search}
              variant="ui"
            />
          </label>
        </div>

        <div className="leads-table-frame">
          <UiTable className="leads-table" variant="ui">
            <caption className="sr-only">All leads</caption>
            <TableHeader>
              <TableRow>
                <TableHead>LEAD</TableHead>
                <TableHead>SOURCE GROUP</TableHead>
                <TableHead className="lead-centered-heading">MEMBER SINCE</TableHead>
                <TableHead>Q&amp;A 1</TableHead>
                <TableHead>Q&amp;A 2</TableHead>
                <TableHead>Q&amp;A 3</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && Array.from({ length: 5 }, (_, index) => (
                <TableRow key={`loading-${index}`}>
                  {Array.from({ length: 6 }, (_, cellIndex) => (
                    <TableCell key={cellIndex}><Skeleton className="lead-row-skeleton" /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!loading && error && <TableRow><TableCell className="lead-state-cell" colSpan="6"><span>{error}</span><Button onClick={() => getTableData()} size="sm" type="button">Retry</Button></TableCell></TableRow>}
              {!loading && !error && !leads.length && (
                <TableRow>
                  <TableCell className="lead-state-cell lead-empty-state-cell" colSpan="6">
                    <EmptyState
                      primaryActionIcon={CirclePlay}
                      primaryActionLabel="Watch setup video"
                      compact
                      description="Your captured leads will appear here after setup."
                      icon={UsersRound}
                      onPrimaryAction={() => setIsSetupVideoOpen(true)}
                      onSecondaryAction={() => window.open(HELP_ARTICLE_URL, "_blank", "noopener,noreferrer")}
                      secondaryActionIcon={BookOpen}
                      secondaryActionLabel="Open help article"
                      title="No leads yet"
                    />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && leads.length > 0 && !visibleLeads.length && (
                <TableRow>
                  <TableCell className="lead-state-cell lead-empty-state-cell" colSpan="6">
                    <EmptyState
                      primaryActionLabel="Reset filters"
                      compact
                      description="Try another search or reset the filters."
                      icon={SearchX}
                      onPrimaryAction={clearFilters}
                      title="No matching leads"
                    />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && paginatedLeads.map((lead) => (
                <LeadRow copiedProfileLeadId={copiedProfileLeadId} key={lead.id} lead={lead} onCopy={copyProfileUrl} />
              ))}
            </TableBody>
          </UiTable>
        </div>

        {!loading && !error && visibleLeads.length > 0 && <div className="leads-pagination" aria-label="Lead pagination">
          <div className="leads-pagination-summary">
            <span className="lead-page-size-label">Leads per page</span>
            <Select className="lead-page-size-select" onValueChange={updatePageSize} value={String(pageSize)}>
              <SelectTrigger aria-label="Leads per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="leads-pagination-controls">
            <Button aria-label="Previous page" className="lead-pagination-button" disabled={currentPage === 0} onClick={() => goToPage(currentPage - 1)} size="icon-sm" type="button" variant="ui"><ChevronLeft size={16} /></Button>
            <div className="lead-page-numbers" aria-label="Select page">
              {Array.from({ length: totalPages }, (_, pageNumber) => (
                <PageNumber currentPage={currentPage} key={pageNumber} onClick={goToPage} page={pageNumber} />
              ))}
            </div>
            <Button aria-label="Next page" className="lead-pagination-button" disabled={currentPage >= totalPages - 1} onClick={() => goToPage(currentPage + 1)} size="icon-sm" type="button" variant="ui"><ChevronRight size={16} /></Button>
          </div>
        </div>}
      </section>
      {isSetupVideoOpen && (
        <HelpVideoDialog
          articleUrl={HELP_ARTICLE_URL}
          description="Watch the quick setup walkthrough, then use the help article whenever you need the written steps."
          onClose={() => setIsSetupVideoOpen(false)}
          title="Set up Groupboss"
          videoUrl={SETUP_VIDEO_URL}
        />
      )}
    </main>
  );
}

export default Table;
